import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AwardBadge from "@/components/AwardBadge";
import { ArrowLeft, ChevronDown, ChevronRight, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Loader2, CheckCircle2, UserCheck, X, Heart, Crown, Share2, Pencil, Eye, EyeOff, BarChart3, Lightbulb } from "lucide-react";
import { useWatchlist, useToggleWatchlist } from "@/hooks/useWatchlist";
import { useFollow } from "@/hooks/useFollow";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Influencer, InfluencerCall, CallEdit } from "@/types/influencer";
import { groupCallsIntoPositions, type Position } from "@/utils/positions";
import { useClassificationProgress } from "@/hooks/useClassificationProgress";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClaimProfileModal from "@/components/ClaimProfileModal";
import FollowSubscriptionModal from "@/components/FollowSubscriptionModal";
import AssetInfoPanel from "@/components/AssetInfoPanel";
import AssetPriceChart from "@/components/AssetPriceChart";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { useLeaderboard } from "@/hooks/useLeaderboard";


interface InfluencerDetailProps {
  influencer: Influencer;
  onBack: () => void;
  verifiedXUserId?: string | null;
  initialSortKey?: SortKey | null;
  initialSortDir?: SortDir | null;
  expandAsset?: string | null;
  expandCallId?: string | null;
  /** When true, hides follow/claim/share/refetch buttons (used for Filters Index) */
  synthetic?: boolean;
}

const validSortKeys: SortKey[] = ["action", "asset", "entryPrice", "exitPrice", "currentPrice", "entryDate", "lastCallDate", "callCount", "status", "openTime", "pnlPercent", "pnlPerDay"];
const validSortDirs: SortDir[] = ["asc", "desc"];

type SortKey = "action" | "asset" | "entryPrice" | "exitPrice" | "currentPrice" | "entryDate" | "lastCallDate" | "callCount" | "status" | "openTime" | "pnlPercent" | "pnlPerDay";
type SortDir = "asc" | "desc";

function getLastCallDate(pos: Position): string {
  if (pos.calls.length === 0) return pos.entryDate;
  return pos.calls.reduce((latest, c) => (c.date > latest ? c.date : latest), pos.calls[0].date);
}

function getOpenTimeMs(pos: Position): number {
  const end = pos.exitDate ? new Date(pos.exitDate).getTime() : Date.now();
  return end - new Date(pos.entryDate).getTime();
}

function formatOpenTime(ms: number): string {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  return `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
}

const sortableHeaders: { key: SortKey; label: string }[] = [
  { key: "action", label: "Type" },
  { key: "asset", label: "Asset" },
  { key: "entryPrice", label: "Entry" },
  { key: "exitPrice", label: "Exit" },
  { key: "currentPrice", label: "Current" },
  { key: "entryDate", label: "Entry Date" },
  { key: "lastCallDate", label: "Last Call" },
  { key: "callCount", label: "Calls" },
  { key: "status", label: "Status" },
  { key: "openTime", label: "Open Time" },
  { key: "pnlPercent", label: "PnL" },
  { key: "pnlPerDay", label: "PnL/day" },
];

function comparePositions(a: Position, b: Position, key: SortKey, dir: SortDir): number {
  let valA: any, valB: any;
  switch (key) {
    case "action": valA = a.action; valB = b.action; break;
    case "asset": valA = a.asset; valB = b.asset; break;
    case "entryPrice": valA = a.entryPrice; valB = b.entryPrice; break;
    case "exitPrice": valA = a.exitPrice ?? -Infinity; valB = b.exitPrice ?? -Infinity; break;
    case "currentPrice": valA = a.currentPrice ?? -Infinity; valB = b.currentPrice ?? -Infinity; break;
    case "entryDate": valA = new Date(a.entryDate).getTime(); valB = new Date(b.entryDate).getTime(); break;
    case "lastCallDate": valA = new Date(getLastCallDate(a)).getTime(); valB = new Date(getLastCallDate(b)).getTime(); break;
    case "callCount": valA = a.calls.length; valB = b.calls.length; break;
    case "status": valA = a.status; valB = b.status; break;
    case "openTime": valA = getOpenTimeMs(a); valB = getOpenTimeMs(b); break;
    case "pnlPercent": valA = a.pnlPercent ?? -Infinity; valB = b.pnlPercent ?? -Infinity; break;
    case "pnlPerDay": valA = a.pnlPerDay ?? -Infinity; valB = b.pnlPerDay ?? -Infinity; break;
  }
  const cmp = typeof valA === "string" ? valA.localeCompare(valB) : valA - valB;
  return dir === "asc" ? cmp : -cmp;
}

const InfluencerDetail = ({ influencer, onBack, verifiedXUserId, initialSortKey, initialSortDir, expandAsset, expandCallId, synthetic }: InfluencerDetailProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isModerator, isAdmin, isPremium } = useUserRole();
  const { isFollowing, toggling, toggle: toggleFollow } = useFollow(influencer.id);
  const { remaining, total, progress } = useClassificationProgress(influencer.id);
  const { data: leaderboard } = useLeaderboard();
  const rank = useMemo(() => {
    if (!leaderboard) return null;
    const sorted = [...leaderboard].sort((a, b) => b.score - a.score);
    const idx = sorted.findIndex((e) => e.id === influencer.id);
    return idx >= 0 ? idx + 1 : null;
  }, [leaderboard, influencer.id]);
  const [claimModalOpen, setClaimModalOpen] = useState(!!verifiedXUserId);
  const [followModalOpen, setFollowModalOpen] = useState(false);
  const [followModalSlide, setFollowModalSlide] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);
  const navigate = useNavigate();

  const navigateToTicker = useCallback((ticker: string) => {
    navigate(`/asset/${encodeURIComponent(ticker)}`);
  }, [navigate]);

  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      const handle = influencer.handle.replace(/^@/, "");
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-tweets`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ handle, full_history: true }),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to re-fetch");
      toast.success(`Re-fetch started: ${data.tweets_fetched} tweets found${data.continued ? " (continuing in background)" : ""}`);
      // Trigger classification
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/classify-tweets`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({}),
        }
      );
      queryClient.invalidateQueries({ queryKey: ["influencer"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    } catch (e: any) {
      toast.error(e.message || "Re-fetch failed");
    } finally {
      setIsRefetching(false);
    }
  };

  const { watchlist, isWatched } = useWatchlist();
  const toggleWatchlist = useToggleWatchlist();

  const positions = useMemo(
    () => groupCallsIntoPositions(influencer.calls),
    [influencer.calls]
  );


  const [assetFilterInput, setAssetFilterInput] = useState("");
  const [actionFilters, setActionFilters] = useState<Set<string>>(() => new Set(["LONG", "SHORT"]));
  const [statusFilter, setStatusFilter] = useState<string | null>(null); // null = all, "OPEN", "CLOSED"
  const [openTimeFilter, setOpenTimeFilter] = useState<number | null>(null); // max ms, null = no filter
  const [callsMin, setCallsMin] = useState<string>("");
  const [callsMax, setCallsMax] = useState<string>("");
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [highlightCallId, setHighlightCallId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>(() => initialSortKey && validSortKeys.includes(initialSortKey) ? initialSortKey : "entryDate");
  const [sortDir, setSortDir] = useState<SortDir>(() => initialSortDir && validSortDirs.includes(initialSortDir) ? initialSortDir : "desc");

  // Columns where ascending is more natural as the first click direction
  const ascFirstColumns: SortKey[] = ["openTime"];

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(ascFirstColumns.includes(key) ? "asc" : "desc");
    }
    setExpandedIdx(null);
  };

  const activeFilters = useMemo(() => {
    if (!assetFilterInput.trim()) return [];
    return assetFilterInput
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
  }, [assetFilterInput]);

  const filtered = useMemo(() => {
    let base = positions.filter((p) => actionFilters.has(p.action));
    if (activeFilters.length > 0) {
      base = base.filter((p) => activeFilters.some((f) => p.asset.toUpperCase().includes(f)));
    }
    if (statusFilter) {
      base = base.filter((p) => p.status === statusFilter);
    }
    if (openTimeFilter !== null) {
      base = base.filter((p) => getOpenTimeMs(p) <= openTimeFilter);
    }
    const minCalls = callsMin ? parseInt(callsMin, 10) : null;
    const maxCalls = callsMax ? parseInt(callsMax, 10) : null;
    if (minCalls !== null && !isNaN(minCalls)) {
      base = base.filter((p) => p.calls.length >= minCalls);
    }
    if (maxCalls !== null && !isNaN(maxCalls)) {
      base = base.filter((p) => p.calls.length <= maxCalls);
    }
    return [...base].sort((a, b) => comparePositions(a, b, sortKey, sortDir));
  }, [positions, activeFilters, actionFilters, statusFilter, openTimeFilter, callsMin, callsMax, sortKey, sortDir]);

  // Auto-expand a specific position from deep links
  const lastAutoExpandKey = useRef<string | null>(null);
  const rowRefs = useRef<Map<number, HTMLElement>>(new Map());
  useEffect(() => {
    if (filtered.length === 0) return;

    const key = expandCallId
      ? `call:${expandCallId}`
      : expandAsset
        ? `asset:${expandAsset.toUpperCase()}`
        : null;

    if (!key || lastAutoExpandKey.current === key) return;

    let idx = -1;

    if (expandCallId) {
      const candidateIndexes = filtered
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.calls.some((c) => c.id === expandCallId));

      if (candidateIndexes.length > 0) {
        idx = candidateIndexes.reduce((best, current) => {
          const bestEntry = new Date(best.p.entryDate).getTime();
          const currentEntry = new Date(current.p.entryDate).getTime();
          return currentEntry > bestEntry ? current : best;
        }).i;
      }
    } else if (expandAsset) {
      idx = filtered.findIndex((p) => p.asset.toUpperCase() === expandAsset.toUpperCase());
    }

    if (idx !== -1) {
      setExpandedIdx(idx);
      lastAutoExpandKey.current = key;
      requestAnimationFrame(() => {
        const el = rowRefs.current.get(idx);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [expandAsset, expandCallId, filtered]);

  const allWithPnl = filtered.filter((p) => p.pnlPercent !== null);
  const allWithNonZeroPnl = allWithPnl.filter((p) => p.pnlPercent !== 0);
  const winRate =
    allWithNonZeroPnl.length > 0
      ? Math.round(
          (allWithNonZeroPnl.filter((p) => (p.pnlPercent ?? 0) > 0).length /
            allWithNonZeroPnl.length) *
            100
        )
      : 0;
  const avgPnl =
    allWithPnl.length > 0
      ? allWithPnl.reduce((s, p) => s + (p.pnlPercent ?? 0), 0) / allWithPnl.length
      : 0;
  const isPositive = avgPnl >= 0;

  const toggle = (i: number) => setExpandedIdx(expandedIdx === i ? null : i);

  const SortIcon = ({ colKey }: { colKey: SortKey }) => {
    if (sortKey !== colKey) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="sticky top-2 z-20 -mx-4 px-4 py-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm bg-card/80 backdrop-blur-sm border border-border rounded-full px-4 py-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {synthetic ? (
            <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-primary" />
            </div>
          ) : (
            <img
              src={influencer.avatar}
              alt={influencer.displayName}
              className="w-16 h-16 rounded-full bg-secondary"
            />
          )}
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">{influencer.displayName} {!synthetic && <AwardBadge handle={influencer.handle} position="after" />}</h2>
            <p className="font-mono text-sm text-muted-foreground">{synthetic ? "Composite of all filtered influencers" : influencer.handle}</p>
          </div>
        </div>
        {!synthetic && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="font-mono gap-2"
            onClick={async () => {
              const shareUrl = `https://finfluenceraudit.pages.dev/profile/${influencer.handle.replace(/^@/, "")}`;
              if (navigator.share) {
                try {
                  await navigator.share({ title: `${influencer.displayName} on TrustFinTwit`, url: shareUrl });
                } catch {}
              } else {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Profile link copied to clipboard!");
              }
            }}
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          {(
            <Button
              variant={isFollowing ? "secondary" : "outline"}
              size="sm"
              className="font-mono gap-2"
              onClick={() => {
                if (isPremium) {
                  toggleFollow();
                } else {
                  setFollowModalSlide(1);
                  setFollowModalOpen(true);
                }
              }}
              disabled={toggling}
            >
              <Heart className={`w-4 h-4 ${isFollowing ? "fill-current text-red-500" : ""}`} />
              {isFollowing ? "Following" : "Follow"}
            </Button>
          )}
          {isModerator && (
            <Button variant="outline" size="sm" className="font-mono gap-2" onClick={handleRefetch} disabled={isRefetching}>
              {isRefetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Re-fetch
            </Button>
          )}
          {influencer.claimed ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-profit/10 border border-profit/30">
              <CheckCircle2 className="w-4 h-4 text-profit" />
              <span className="font-mono text-sm font-semibold text-profit">Trusted</span>
            </div>
          ) : user ? (
            <Button variant="outline" size="sm" className="font-mono gap-2" onClick={() => setClaimModalOpen(true)}>
              <UserCheck className="w-4 h-4" />
              Claim this profile
            </Button>
          ) : null}
        </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Rank", value: rank ? `#${rank}` : "—", color: "text-primary" },
          { label: "Avg PnL", value: allWithPnl.length > 0 ? `${avgPnl > 0 ? "+" : ""}${avgPnl.toFixed(1)}%` : "—", color: avgPnl > 0 ? "text-profit" : avgPnl < 0 ? "text-loss" : "text-muted-foreground" },
          { label: "Win Rate", value: allWithPnl.length > 0 ? `${winRate}%` : "—", color: winRate > 0 ? (winRate >= 50 ? "text-profit" : "text-loss") : "text-muted-foreground" },
          { label: "PnL/day", value: (() => { const v = filtered.filter(p => p.pnlPerDay != null && !isNaN(p.pnlPerDay)); if (v.length === 0) return "—"; const avg = v.reduce((s, p) => s + (p.pnlPerDay ?? 0), 0) / v.length; return `${avg > 0 ? "+" : ""}${avg.toFixed(2)}%`; })(), color: "text-foreground" },
          { label: "Open / Closed", value: `${filtered.filter((p) => p.status === "OPEN" && p.action !== "WATCH" && p.action !== "HOLD").length} / ${filtered.filter((p) => p.status === "CLOSED").length}`, color: "text-foreground" },
        ].map((stat) => (
          <div key={stat.label} className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-1">{stat.label}</div>
            <div className={`font-mono font-bold text-lg ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>


      {/* Tweet fetching / Classification Progress */}
      {!synthetic && remaining !== null && total !== null && total === 0 && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <span className="text-muted-foreground">Fetching tweets...</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Scanning timeline for cashtag mentions. This may take a moment for accounts with extensive history.
          </p>
        </div>
      )}

      {!synthetic && remaining !== null && remaining > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <span className="text-muted-foreground">Classifying tweets...</span>
            </div>
            <span className="font-mono text-xs text-muted-foreground">
              {total !== null ? total - remaining : 0} / {total ?? 0} tweets
            </span>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {remaining} tweets remaining. Classification runs automatically in the background.
          </p>
        </div>
      )}

      {/* Call Audit Log */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-foreground">Call Audit Log</h3>
          <input
            type="text"
            value={assetFilterInput}
            onChange={(e) => setAssetFilterInput(e.target.value)}
            placeholder="Filter assets (e.g. BTC, ETH, SOL)"
            className="bg-card border border-border rounded-lg px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full sm:w-64 placeholder:text-muted-foreground"
          />
        </div>

        {/* Action type filter toggles */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs text-muted-foreground font-mono mr-1 w-full sm:w-[6.5rem] shrink-0">Filter Type:</span>
          {([
            { key: "LONG", onClass: "bg-profit/15 text-profit border-profit/30", label: "Long" },
            { key: "SHORT", onClass: "bg-loss/15 text-loss border-loss/30", label: "Short" },
            { key: "HOLD", onClass: "bg-yellow-400/15 text-yellow-500 border-yellow-500/30", label: "Hold" },
            { key: "WATCH", onClass: "bg-muted text-muted-foreground border-border", label: "Watch", premium: true },
          ] as const).map(({ key, onClass, label, ...rest }) => {
            const isPremiumFeature = "premium" in rest && rest.premium;
            const isOn = actionFilters.has(key);
            return (
              <button
                key={key}
                onClick={() => {
                  if (isPremiumFeature && !isPremium) {
                    setFollowModalSlide(0);
                    setFollowModalOpen(true);
                    return;
                  }
                  setActionFilters((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                  setExpandedIdx(null);
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-mono font-semibold border transition-colors flex items-center gap-1 ${
                  isOn ? onClass : "bg-muted/30 text-muted-foreground/50 border-transparent"
                }`}
              >
                {label}
                {isPremiumFeature && !isPremium && (
                  <Crown className="w-3 h-3 text-yellow-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs text-muted-foreground font-mono mr-1 w-full sm:w-[6.5rem] shrink-0">Filter Status:</span>
          {([
            { key: null, label: "All" },
            { key: "OPEN", label: "Open" },
            { key: "CLOSED", label: "Closed" },
          ] as const).map(({ key, label }) => (
            <button
              key={label}
              onClick={() => { setStatusFilter(key); setExpandedIdx(null); }}
              className={`px-2.5 py-1 rounded-full text-xs font-mono font-semibold border transition-colors ${
                statusFilter === key
                  ? "bg-primary/15 text-primary border-primary/30"
                  : "bg-muted/30 text-muted-foreground/50 border-transparent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Open Time filter */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-muted-foreground font-mono mr-1 w-full sm:w-[6.5rem] shrink-0">Filter Time:</span>
          {([
            { key: null, label: "All" },
            { key: 8 * 60 * 60 * 1000, label: "< 8 hours" },
            { key: 24 * 60 * 60 * 1000, label: "< 1 day" },
            { key: 7 * 24 * 60 * 60 * 1000, label: "< 1 week" },
            { key: 30 * 24 * 60 * 60 * 1000, label: "< 1 month" },
            { key: 365 * 24 * 60 * 60 * 1000, label: "< 1 year" },
          ] as const).map(({ key, label }) => (
            <button
              key={label}
              onClick={() => { setOpenTimeFilter(key); setExpandedIdx(null); }}
              className={`px-2.5 py-1 rounded-full text-xs font-mono font-semibold border transition-colors ${
                openTimeFilter === key
                  ? "bg-primary/15 text-primary border-primary/30"
                  : "bg-muted/30 text-muted-foreground/50 border-transparent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Calls filter */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-muted-foreground font-mono mr-1 w-full sm:w-[6.5rem] shrink-0">Filter Calls:</span>
          <input
            type="number"
            min="1"
            value={callsMin}
            onChange={(e) => { setCallsMin(e.target.value); setExpandedIdx(null); }}
            placeholder="Min"
            className="bg-card border border-border rounded-lg px-2.5 py-1 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-16 placeholder:text-muted-foreground"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <input
            type="number"
            min="1"
            value={callsMax}
            onChange={(e) => { setCallsMax(e.target.value); setExpandedIdx(null); }}
            placeholder="Max"
            className="bg-card border border-border rounded-lg px-2.5 py-1 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-16 placeholder:text-muted-foreground"
          />
        </div>

        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          {/* Table header */}
          <div className="grid grid-cols-[28px_72px_80px_95px_95px_95px_100px_100px_50px_65px_80px_85px_80px] gap-2 px-4 py-3 border-b border-border">
            <span />
            {sortableHeaders.map((h) => (
              <button
                key={h.key}
                onClick={() => handleSort(h.key)}
                className={`font-mono text-[10px] uppercase tracking-widest flex items-center gap-1 hover:text-foreground transition-colors ${
                  h.key === "pnlPercent" || h.key === "pnlPerDay" || h.key === "openTime" ? "justify-end" : ""
                } ${sortKey === h.key ? "text-profit" : "text-muted-foreground"}`}
              >
                {h.label}
                <SortIcon colKey={h.key} />
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-mono text-sm">
              No positions found.
            </div>
          )}

          {filtered.map((pos, i) => (
            <div key={`${pos.asset}-${pos.entryDate}-${i}`} ref={(el) => { if (el) rowRefs.current.set(i, el); else rowRefs.current.delete(i); }}>
              {/* Position row */}
              <div
                onClick={() => toggle(i)}
                className={`grid grid-cols-[28px_72px_80px_95px_95px_95px_100px_100px_50px_65px_80px_85px_80px] gap-2 px-4 py-3 border-b border-border transition-colors cursor-pointer items-center ${
                  expandedIdx === i ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-secondary/50"
                }`}
              >
                <span className="text-muted-foreground">
                  {expandedIdx === i ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </span>
                <span
                  className={`font-mono text-xs font-bold px-2 py-0.5 rounded w-fit ${
                    pos.action === "LONG" ? "bg-profit/15 text-profit" : pos.action === "SHORT" ? "bg-loss/15 text-loss" : pos.action === "HOLD" ? "bg-yellow-400/15 text-yellow-500" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {pos.action}
                </span>
                <span className="font-mono text-sm font-bold text-foreground flex items-center gap-1">
                  <span
                    className="hover:text-primary hover:underline cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigateToTicker(pos.asset); }}
                  >{pos.asset}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleWatchlist.mutate({ ticker: pos.asset, isWatched: isWatched(pos.asset) }); }}
                    className={`transition-colors ${isWatched(pos.asset) ? "text-primary" : "text-muted-foreground/40 hover:text-primary"}`}
                    title={isWatched(pos.asset) ? "Remove from watchlist" : "Add to watchlist"}
                  >
                    {isWatched(pos.asset) ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                </span>
                <span
                  className="font-mono text-sm text-foreground"
                  title={pos.entryPriceSourceInterval && pos.entryPriceSourceTimestamp
                    ? `${pos.entryPriceSourceInterval} candle · ${new Date(pos.entryPriceSourceTimestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
                    : undefined}
                >
                  {pos.entryPrice > 0 ? `$${pos.entryPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                </span>
                <span
                  className="font-mono text-sm text-foreground"
                  title={pos.exitPriceSourceInterval && pos.exitPriceSourceTimestamp
                    ? `${pos.exitPriceSourceInterval} candle · ${new Date(pos.exitPriceSourceTimestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
                    : undefined}
                >
                  {pos.exitPrice !== null
                    ? `$${pos.exitPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "—"}
                </span>
                <span
                  className="font-mono text-sm text-foreground"
                  title={pos.currentPrice !== null ? "Live price from market data" : undefined}
                >
                  {pos.currentPrice !== null
                    ? `$${pos.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "—"}
                </span>
                <span className="font-mono text-sm text-foreground">
                  {new Date(pos.entryDate).toLocaleDateString("en-US")}
                </span>
                <span className="font-mono text-sm text-foreground">
                  {new Date(getLastCallDate(pos)).toLocaleDateString("en-US")}
                </span>
                <span className="font-mono text-sm text-center text-muted-foreground">
                  {pos.calls.length}
                </span>
                <span className={`font-mono text-sm font-semibold ${pos.status === "OPEN" && pos.action !== "WATCH" && pos.action !== "HOLD" ? "text-blue-400" : "text-muted-foreground"}`}>
                  {pos.action === "WATCH" || pos.action === "HOLD" ? "—" : pos.status}
                </span>
                <span className="font-mono text-xs text-muted-foreground whitespace-nowrap ml-auto">
                  {formatOpenTime(getOpenTimeMs(pos))}
                </span>
                {(() => {
                  const pnlText = pos.pnlPercent !== null ? `${pos.pnlPercent >= 0 ? "+" : ""}${pos.pnlPercent.toFixed(2)}%` : "—";
                  const comparePrice = pos.status === "CLOSED" ? pos.exitPrice : pos.currentPrice;
                  const formula = pos.pnlPercent !== null && comparePrice !== null
                    ? pos.action === "LONG"
                      ? `(${comparePrice.toFixed(2)} - ${pos.entryPrice.toFixed(2)}) / ${pos.entryPrice.toFixed(2)} × 100`
                      : `(${pos.entryPrice.toFixed(2)} - ${comparePrice.toFixed(2)}) / ${pos.entryPrice.toFixed(2)} × 100`
                    : null;
                  return (
                    <span
                      className={`font-mono text-sm font-bold text-right ${
                        pos.pnlPercent !== null ? (pos.pnlPercent > 0 ? "text-profit" : pos.pnlPercent < 0 ? "text-loss" : "text-muted-foreground") : "text-muted-foreground"
                      }`}
                      title={formula ?? undefined}
                    >
                      {pnlText}
                    </span>
                  );
                })()}
                {/* PnL/day */}
                {(() => {
                  const pnlDay = pos.pnlPerDay;
                  const text = pnlDay !== null ? `${pnlDay >= 0 ? "+" : ""}${pnlDay.toFixed(2)}%` : "—";
                  return (
                    <span
                      className={`font-mono text-sm font-bold text-right ${
                        pnlDay !== null ? (pnlDay > 0 ? "text-profit" : pnlDay < 0 ? "text-loss" : "text-muted-foreground") : "text-muted-foreground"
                      }`}
                    >
                      {text}
                    </span>
                  );
                })()}
              </div>

              {/* Expanded inline call details */}
              {expandedIdx === i && (
                <div className="border-b border-border bg-secondary/30 border-l-2 border-l-primary">
                  {/* Asset company info */}
                  <div className="px-6 py-4 border-b border-border/50">
                    <AssetInfoPanel ticker={pos.asset} />
                  </div>
                  {/* Price chart */}
                  <div className="border-b border-border/50">
                    <AssetPriceChart
                      ticker={pos.asset}
                      calls={pos.calls}
                      onCallClick={(callId) => setHighlightCallId(callId)}
                    />
                  </div>
                  {/* Calls list */}
                  <div className="px-6 py-3">
                    <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Calls ({pos.calls.length})</h4>
                    <div className="grid grid-cols-[28px_72px_80px_100px_150px_1fr] gap-2 px-4 py-2 border-b border-border/50">
                      <span />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Type</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Asset</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Price</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Date</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tweet</span>
                    </div>
                    <CallList calls={pos.calls} highlightCallId={highlightCallId} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ClaimProfileModal
        open={claimModalOpen}
        onOpenChange={setClaimModalOpen}
        handle={influencer.handle}
        profileXUserId={influencer.id}
        initialVerifiedXUserId={verifiedXUserId ?? undefined}
      />
      <FollowSubscriptionModal
        open={followModalOpen}
        onOpenChange={setFollowModalOpen}
        handle={influencer.handle}
        initialSlide={followModalSlide}
      />
    </div>
  );
};

function CallList({ calls, highlightCallId }: { calls: InfluencerCall[]; highlightCallId?: string | null }) {
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const sorted = useMemo(
    () => [...calls].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [calls]
  );
  return (
    <div className="space-y-0">
      {sorted.map((call) => (
        <CallRow
          key={call.id}
          call={call}
          expanded={expandedCallId === call.id}
          onToggle={() => setExpandedCallId(expandedCallId === call.id ? null : call.id)}
          highlighted={highlightCallId === call.id}
        />
      ))}
    </div>
  );
}

function HighlightTicker({ text, ticker }: { text: string; ticker: string }) {
  if (!text || !ticker) return <>{text}</>;
  const escaped = ticker.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`(\\$?${escaped})`, "gi");
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) => {
        const match = new RegExp(`^\\$?${escaped}$`, "i").test(part);
        return match ? (
          <span key={i} className="text-primary font-bold not-italic">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

/** Strip trailing t.co URLs from tweet text */
function stripTrailingUrls(text: string): string {
  return text.replace(/\s*https?:\/\/t\.co\/\w+\s*$/g, "").trim();
}

function CallRow({ call, expanded, onToggle, highlighted }: { call: InfluencerCall; expanded: boolean; onToggle: () => void; highlighted?: boolean }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const navigate = useNavigate();
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isReanalyzingAll, setIsReanalyzingAll] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [reanalyzeMode, setReanalyzeMode] = useState<"single" | "all">("single");
  const [userMessage, setUserMessage] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editAction, setEditAction] = useState<string>(call.action);
  const [editComment, setEditComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showThesisDialog, setShowThesisDialog] = useState(false);
  const [thesisRemarks, setThesisRemarks] = useState("");
  const queryClient = useQueryClient();
  const { isModerator, isPremium } = useUserRole();
  const { user } = useAuth();
  const { isWatched } = useWatchlist();
  const toggleWatchlist = useToggleWatchlist();
  const isSell = call.action === "SELL" || call.action === "TRIM" || call.action === "CLOSE";
  const priceStr = call.priceAtCall !== null
    ? `$${call.priceAtCall.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
  const priceTooltip = call.priceSourceInterval && call.priceSourceTimestamp
    ? `${call.priceSourceInterval} candle · ${new Date(call.priceSourceTimestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`
    : undefined;

  const hasEdits = call.edits && call.edits.length > 0;

  const handleReanalyzeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUserMessage("");
    setReanalyzeMode("single");
    setShowMessageDialog(true);
  };

  const handleReanalyzeAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUserMessage("");
    setReanalyzeMode("all");
    setShowMessageDialog(true);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditAction(call.action);
    setEditComment("");
    setShowEditDialog(true);
  };

  const handleThesisClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setThesisRemarks("");
    setShowThesisDialog(true);
  };

  const handleThesisSubmit = () => {
    const tweetPart = call.tweetSnippet ? stripTrailingUrls(call.tweetSnippet) : `${call.action} ${call.asset}`;
    const parts = [`Tweet from @${call.asset}'s profile: "${tweetPart}"`];
    if (thesisRemarks.trim()) parts.push(`My thoughts: ${thesisRemarks.trim()}`);
    navigate(`/thesis?message=${encodeURIComponent(parts.join("\n\n"))}`);
  };

  const handleEditSubmit = async () => {
    if (!editComment.trim()) {
      toast.error("A comment is required for manual edits");
      return;
    }
    setIsEditing(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/edit-call`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            call_id: call.id,
            new_action: editAction,
            comment: editComment.trim(),
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Edit failed");
      }
      const result = await resp.json();
      toast.success(`${call.asset}: ${result.old_action} → ${result.new_action}`);
      setShowEditDialog(false);
      await queryClient.invalidateQueries({ queryKey: ["influencer"] });
      await queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Edit failed");
    } finally {
      setIsEditing(false);
    }
  };

  const handleReanalyze = async () => {
    setShowMessageDialog(false);
    const isAll = reanalyzeMode === "all";
    if (isAll) setIsReanalyzingAll(true); else setIsReanalyzing(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const funcName = isAll ? "reanalyze-all-from-tweet" : "reanalyze-call";
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${funcName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            call_id: call.id,
            ...(userMessage.trim() ? { user_message: userMessage.trim() } : {}),
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Reanalysis failed");
      }
      const result = await resp.json();
      if (isAll) {
        const changed = (result.updates || []).filter((u: any) => u.changed);
        if (changed.length > 0) {
          toast.success(`Updated ${changed.length} call(s): ${changed.map((u: any) => `${u.asset}: ${u.old_action}→${u.new_action}`).join(", ")}`);
        } else {
          toast.info("All classifications unchanged");
        }
      } else {
        if (result.changed) {
          toast.success(`${call.asset}: ${result.old_action} → ${result.new_action}`);
        } else {
          toast.info(`${call.asset}: classification unchanged (${result.old_action})`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["influencer"] });
      await queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Reanalysis failed");
    } finally {
      setIsReanalyzing(false);
      setIsReanalyzingAll(false);
    }
  };

  return (
    <div>
      <div
        onClick={() => call.tweetSnippet && onToggle()}
        className={`grid grid-cols-[28px_72px_80px_100px_150px_1fr] gap-2 px-4 py-2 items-center text-muted-foreground ${call.tweetSnippet ? "cursor-pointer hover:bg-secondary/30 transition-colors" : ""} ${highlighted ? "bg-primary/10 ring-1 ring-primary/30" : ""}`}
      >
        <span className="text-muted-foreground">
          {call.tweetSnippet ? (expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : <span />}
        </span>
        <span
        className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded w-fit ${
          call.action === "BUY"
            ? "bg-profit/10 text-profit/70"
            : call.action === "HOLD"
              ? "bg-yellow-500/15 text-yellow-400"
              : call.action === "WATCH"
                ? "bg-muted text-muted-foreground"
                : call.action === "TRIM"
                  ? "bg-red-400/15 text-red-400"
                    : call.action === "NOISE"
                     ? "bg-muted text-muted-foreground/60"
                     : call.action === "CLOSE"
                       ? "bg-orange-400/15 text-orange-400"
                       : "bg-loss/10 text-loss/70"
        }`}
        >
          {call.action}
          {hasEdits && <Pencil className="w-2.5 h-2.5 inline ml-1 opacity-60" />}
        </span>
        <span className="font-mono text-xs flex items-center gap-1">
          <span
            className="hover:text-primary hover:underline cursor-pointer"
            onClick={(e) => { e.stopPropagation(); navigate(`/asset/${encodeURIComponent(call.asset)}`); }}
          >{call.asset}</span>
          <button
            onClick={(e) => { e.stopPropagation(); toggleWatchlist.mutate({ ticker: call.asset, isWatched: isWatched(call.asset) }); }}
            className={`transition-colors ${isWatched(call.asset) ? "text-primary" : "text-muted-foreground/40 hover:text-primary"}`}
            title={isWatched(call.asset) ? "Remove from watchlist" : "Add to watchlist"}
          >
            {isWatched(call.asset) ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </button>
        </span>
        <span className="font-mono text-xs" title={priceTooltip}>{priceStr}</span>
        <span className="font-mono text-xs">
          {new Date(call.date).toLocaleDateString("en-US")}{" "}
          {new Date(call.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
        </span>
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-xs italic truncate">
            "<HighlightTicker text={call.tweetSnippet} ticker={call.asset} />"
          </span>
          {isPremium && (
            <button
              onClick={handleThesisClick}
              className="text-muted-foreground/40 hover:text-primary transition-colors shrink-0"
              title="Use in thesis"
            >
              <Lightbulb className="w-3.5 h-3.5" />
            </button>
          )}
          {isModerator && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={handleEditClick}
                disabled={isEditing}
                className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                title="Edit this call"
              >
                <Pencil className={`w-3 h-3`} />
              </button>
              <button
                onClick={handleReanalyzeClick}
                disabled={isReanalyzing || isReanalyzingAll}
                className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                title="Re-analyze this call"
              >
                <RefreshCw className={`w-3 h-3 ${isReanalyzing ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={handleReanalyzeAllClick}
                disabled={isReanalyzing || isReanalyzingAll}
                className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                title="Re-analyze all tickers from this tweet"
              >
                {isReanalyzingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : "All"}
              </button>
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-2 pl-[110px] space-y-2">
          <p className="font-mono text-xs text-muted-foreground italic whitespace-pre-wrap">
            "<HighlightTicker text={call.tweetSnippet} ticker={call.asset} />"
          </p>
          {call.mediaUrl && (
            <>
              <img
                src={call.mediaUrl}
                alt="Tweet media"
                className="rounded-lg border border-border max-w-[280px] max-h-[200px] object-contain cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setLightboxOpen(true)}
              />
              <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-2 bg-background/95 flex items-center justify-center">
                  <img
                    src={call.mediaUrl}
                    alt="Tweet media"
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
          {/* Edit history */}
          {hasEdits && (
            <div className="space-y-1">
              {call.edits!.map((edit) => (
                <div key={edit.id} className="flex items-start gap-2 text-[10px] font-mono text-muted-foreground">
                  <Pencil className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground/60" />
                  <div>
                    <span className="font-semibold text-foreground/80">
                      {edit.editType === "manual" ? "manual edit" : edit.editType === "reanalyze" ? "reanalyze" : "reanalyze all"}
                    </span>
                    {edit.editorEmail && (
                      <span> by <span className="text-primary/80">{edit.editorEmail}</span></span>
                    )}
                    <span> — </span>
                    <span className="text-loss">{edit.oldAction}</span>
                    <span> → </span>
                    <span className="text-profit">{edit.newAction}</span>
                    {edit.comment && (
                      <span className="italic"> — "{edit.comment}"</span>
                    )}
                    <span className="ml-2 opacity-60">
                      {new Date(edit.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}{" "}
                      {new Date(edit.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Reanalyze dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{reanalyzeMode === "all" ? "Re-analyze all tickers" : `Re-analyze: ${call.asset}`}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground italic truncate">"{call.tweetSnippet}"</p>
          <Textarea
            placeholder="Add context for the AI (optional)..."
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            className="min-h-[80px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button variant={reanalyzeMode === "all" ? "secondary" : "default"} onClick={handleReanalyze}>
              {reanalyzeMode === "all" ? "Re-analyze All" : "Re-analyze"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit call dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Edit Call: {call.asset}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Current action: <span className="font-mono font-bold">{call.action}</span></label>
              <Select value={editAction} onValueChange={setEditAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["BUY", "SHORT", "HOLD", "TRIM", "WATCH", "NOISE", "CLOSE"].map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Reason (required)</label>
              <Textarea
                placeholder="Why are you changing this call?"
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} disabled={isEditing || !editComment.trim()}>
              {isEditing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Use in thesis dialog */}
      <Dialog open={showThesisDialog} onOpenChange={setShowThesisDialog}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Use in Thesis</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Tweet</label>
              <p className="text-xs text-foreground italic border border-border rounded-md p-2 bg-muted/30">
                "{call.tweetSnippet ? stripTrailingUrls(call.tweetSnippet) : `${call.action} ${call.asset}`}"
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono font-bold">{call.asset}</span>
              <span className="font-mono px-1.5 py-0.5 rounded bg-muted">{call.action}</span>
              <span>{new Date(call.date).toLocaleDateString("en-US")}</span>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Your thoughts (optional)</label>
              <Textarea
                placeholder="Add your own analysis or context..."
                value={thesisRemarks}
                onChange={(e) => setThesisRemarks(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowThesisDialog(false)}>Cancel</Button>
            <Button onClick={handleThesisSubmit} className="gap-1.5">
              <Lightbulb className="w-4 h-4" />
              Send to Thesis Builder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
export default InfluencerDetail;
